import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { NotFound } from '../NotFound';
import { PageHeader, PageShell } from '../../components/ui/PageHeader';
import { TabBar, type TabItem } from '../../components/ui/Tabs';
import { useAccess } from '../../hooks/useAccess';
import { CsmCustomers } from '../Dashboard/CsmDashboard';
import { AtRiskTab } from '../Dashboard/csm/AtRiskTab';
import { UsageTab } from '../Dashboard/csm/UsageTab';
import { SnapshotsTab } from '../Dashboard/csm/SnapshotsTab';

type CsmTab = 'customers' | 'at-risk' | 'usage' | 'snapshots';

const TEAM_TABS: readonly TabItem<CsmTab>[] = [
  { id: 'customers', label: 'Customers' },
  { id: 'at-risk', label: 'At risk' },
  { id: 'usage', label: 'Usage' },
];

/** Snapshot history is a pipeline check, not a team view. */
const ADMIN_TABS: readonly TabItem<CsmTab>[] = [{ id: 'snapshots', label: 'Snapshot history' }];

/** URLs from the old 3-tab / 5-sub-tab layout, kept working. */
const LEGACY_REDIRECTS: Record<string, string> = {
  'token-usage': '/csm/customers',
  'token-usage-aos': '/csm/usage?platform=aos',
  // CSM › Subscriptions rendered Finance's own tab; Finance owns it now.
  subscriptions: '/finance/subscriptions',
};

/** CSM — who we serve, who is slipping, and how they use the platform. */
export function Csm() {
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();
  const { isAdmin } = useAccess();

  const tabs = isAdmin ? [...TEAM_TABS, ...ADMIN_TABS] : TEAM_TABS;

  if (tab && LEGACY_REDIRECTS[tab]) {
    return <Navigate to={LEGACY_REDIRECTS[tab]} replace />;
  }
  if (tab && ![...TEAM_TABS, ...ADMIN_TABS].some((t) => t.id === tab)) return <NotFound />;
  if (!tab || !tabs.some((t) => t.id === tab)) {
    return <Navigate to="/csm/customers" replace />;
  }
  const active = tab as CsmTab;

  return (
    <PageShell>
      <PageHeader eyebrow="Departments" title="CSM" subtitle="Who we serve, who is slipping, and how they use the platform.">
        <TabBar tabs={tabs} active={active} onSelect={(id) => navigate(`/csm/${id}`)} />
      </PageHeader>
      {active === 'customers' && <CsmCustomers />}
      {active === 'at-risk' && <AtRiskTab />}
      {active === 'usage' && <UsageTab />}
      {active === 'snapshots' && <SnapshotsTab />}
    </PageShell>
  );
}
